import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CircleNotch, MagicWand } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";

export function SuggestionTester({ businesses, initialBusinessId }) {
  const [businessId, setBusinessId] = useState(initialBusinessId || "");
  const [description, setDescription] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (initialBusinessId) setBusinessId(initialBusinessId);
    else if (!businessId && businesses[0]) setBusinessId(businesses[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialBusinessId, businesses]);

  const run = async () => {
    if (!businessId || !description.trim()) return toast.error("Pick a business and type a description first.");
    setBusy(true);
    try {
      const { data } = await api.get("/receipt-rules/suggest", { params: { business_id: businessId, description } });
      setResult(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't test that description.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bk-card mt-6 p-5" data-testid="receipt-rules-tester">
      <div className="flex items-center gap-2">
        <MagicWand size={16} weight="fill" color="var(--bk-accent)" />
        <p className="text-xs uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Test a description</p>
      </div>
      <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>See which category Rowan would suggest for a receipt line today, using only your rules — no receipt needed.</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <select value={businessId} onChange={(e) => { setBusinessId(e.target.value); setResult(null); }} data-testid="receipt-rules-tester-business" className="rounded-md border px-3 py-2 text-sm bg-white" style={{ borderColor: "var(--bk-border)" }}>
          {businesses.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <input value={description} onChange={(e) => { setDescription(e.target.value); setResult(null); }} onKeyDown={(e) => e.key === "Enter" && run()} placeholder="e.g. Nitrile gloves box" data-testid="receipt-rules-tester-input" className="flex-1 min-w-[220px] rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
        <button onClick={run} disabled={busy} data-testid="receipt-rules-tester-run" className="bk-btn-primary px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60">
          {busy ? <CircleNotch size={14} className="animate-spin" /> : <MagicWand size={14} weight="bold" />} What would Rowan suggest?
        </button>
      </div>
      {result && (
        <p className="mt-3 text-sm" data-testid="receipt-rules-tester-result" style={{ color: "var(--bk-text)" }}>
          {result.category ? <>Rowan would suggest <strong data-testid="receipt-rules-tester-category">{result.category}</strong></> : <strong data-testid="receipt-rules-tester-category">No rule matches</strong>}
          <span style={{ color: "var(--bk-text-2)" }}> — {result.source_label}.</span>
        </p>
      )}
    </section>
  );
}
